import { ReactNode } from 'react';
import { FileLock, LucideBell, LucideKeyRound, LucideShieldCheck } from 'lucide-react';
import {
    TbAdjustments,
    TbArticle,
    TbBrowserCheck,
    TbBrush,
    TbChecklist,
    TbCloudLock,
    TbCloudUpload,
    TbCode,
    TbCreditCard,
    TbDatabase,
    TbDeviceDesktop,
    TbFocusCentered,
    TbGift,
    TbInfoCircle,
    TbLockAccess,
    TbPlugConnected,
    TbPuzzle,
    TbPyramid,
    TbSettingsSearch,
    TbShieldLock,
    TbTools,
    TbUserCog,
    TbUsers,
    TbUserScreen,
    TbUsersGroup,
    TbUserShield,
    TbWebhook,
    TbWorldCheck,
} from 'react-icons/tb';

import { ACCOUNT_NOTIFICATION_SETTINGS_PATHS } from '@/shared/constants';

import { buildNotificationGroups } from './layout/02-Content/routes/Account/03-Notifications/notificationGroups';

export type SettingsSearchEntryType = 'page' | 'tab' | 'section' | 'field';

export interface SettingsSearchEntry {
    aliases?: string[];
    children?: SettingsSearchEntry[];
    description?: string;
    icon?: ReactNode;
    key: string;
    label: string;
    route: string;
    type: SettingsSearchEntryType;
}

export interface SettingsSearchPage extends SettingsSearchEntry {
    sectionKey: 'account' | 'workspace';
    type: 'page';
}

const iconStyle = { marginRight: '0.375rem', verticalAlign: '-0.175rem' } as const;
const notificationSettingsGroups = buildNotificationGroups();

export const settingsSearchSections = [
    {
        key: 'account',
        label: 'Account',
    },
    {
        key: 'workspace',
        label: 'Workspace',
    },
] as const;

export const settingsSearchPages: SettingsSearchPage[] = [
    {
        key: 'account-general',
        label: 'General',
        route: '/settings/account/general/overview',
        sectionKey: 'account',
        type: 'page',
        icon: <TbUserCog size='1rem' style={iconStyle} />,
        aliases: ['profile', 'user', 'account information'],
        children: [
            {
                key: 'account-general-overview',
                label: 'Overview',
                route: '/settings/account/general/overview',
                type: 'tab',
                icon: <TbUserScreen size='1rem' style={iconStyle} />,
                children: [
                    {
                        key: 'account-general-account-information',
                        label: 'Account Information',
                        route: '/settings/account/general/overview',
                        type: 'section',
                        aliases: ['profile', 'contact information', 'account profile'],
                        children: [
                            {
                                key: 'account-general-avatar',
                                label: 'Avatar',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['profile photo', 'account image'],
                            },
                            {
                                key: 'account-general-first-name',
                                label: 'First Name',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['given name', 'profile name'],
                            },
                            {
                                key: 'account-general-last-name',
                                label: 'Last Name',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['surname', 'family name', 'profile name'],
                            },
                            {
                                key: 'account-general-email',
                                label: 'Email Address',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['email', 'account email'],
                            },
                            {
                                key: 'account-general-verify',
                                label: 'Verify Account',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['email verification', 'verify email'],
                            },
                            {
                                key: 'account-general-username',
                                label: 'Username',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['handle', 'account handle'],
                            },
                            {
                                key: 'account-general-company',
                                label: 'Company',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['organization', 'employer', 'company search'],
                            },
                            {
                                key: 'account-general-title',
                                label: 'Title',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['role', 'job title'],
                            },
                            {
                                key: 'account-general-bio',
                                label: 'Bio',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['biography', 'profile description'],
                            },
                            {
                                key: 'account-general-calendar-link',
                                label: 'Calendar Link',
                                route: '/settings/account/general/overview',
                                type: 'field',
                                aliases: ['calendar', 'booking link'],
                            },
                        ],
                    },
                    {
                        key: 'account-general-danger-zone',
                        label: 'Danger Zone',
                        route: '/settings/account/general/overview',
                        type: 'section',
                        aliases: ['delete account', 'remove account'],
                    },
                ],
            },
            {
                key: 'account-general-data-sources',
                label: 'Data Sources',
                route: '/settings/account/general/connections',
                type: 'tab',
                icon: <TbDatabase size='1rem' style={iconStyle} />,
                aliases: ['connections', 'integrations', 'connected accounts'],
                children: [
                    {
                        key: 'account-general-data-sources-section',
                        label: 'Data Sources',
                        route: '/settings/account/general/connections',
                        type: 'section',
                        children: [
                            {
                                key: 'account-general-account-integrations',
                                label: 'Account Integrations',
                                route: '/settings/account/general/connections',
                                type: 'field',
                                aliases: ['connect data source', 'oauth', 'sync'],
                                children: [
                                    {
                                        key: 'account-general-add-data-source',
                                        label: 'Add Data Source',
                                        route: '/settings/account/general/connections',
                                        type: 'field',
                                    },
                                    {
                                        key: 'account-general-manage-data-sources',
                                        label: 'Manage Data Sources',
                                        route: '/settings/account/general/connections',
                                        type: 'field',
                                    },
                                ],
                            },
                            {
                                key: 'account-general-existing-integrations',
                                label: 'Existing Integrations',
                                route: '/settings/account/general/connections',
                                type: 'field',
                                aliases: ['connected data sources', 'existing accounts'],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        key: 'account-preferences',
        label: 'Preferences',
        route: '/settings/account/preferences',
        sectionKey: 'account',
        type: 'page',
        icon: <TbTools size='1rem' style={iconStyle} />,
        aliases: ['theme', 'timezone', 'display'],
        children: [
            {
                key: 'account-preferences-overview',
                label: 'Overview',
                route: '/settings/account/preferences/overview',
                type: 'tab',
                icon: <TbTools size='1rem' style={iconStyle} />,
                children: [
                    {
                        key: 'account-preferences-theme-timezone',
                        label: 'Theme & Timezone',
                        route: '/settings/account/preferences/overview',
                        type: 'section',
                        aliases: ['theme', 'preferred timezone', 'appearance'],
                        children: [
                            {
                                key: 'account-preferences-theme',
                                label: 'Theme',
                                route: '/settings/account/preferences/overview',
                                type: 'field',
                            },
                            {
                                key: 'account-preferences-timezone',
                                label: 'Preferred Timezone',
                                route: '/settings/account/preferences/overview',
                                type: 'field',
                            },
                        ],
                    },
                    {
                        key: 'account-preferences-reset',
                        label: 'Reset Preferences',
                        route: '/settings/account/preferences/overview',
                        type: 'section',
                        aliases: ['reset workspace preferences', 'clear local settings'],
                        children: [
                            {
                                key: 'account-preferences-reset-workspace',
                                label: 'Reset Workspace Preferences',
                                route: '/settings/account/preferences/overview',
                                type: 'field',
                            },
                        ],
                    },
                ],
            },
            {
                key: 'account-preferences-tags',
                label: 'Tags',
                route: '/settings/account/preferences/tags',
                type: 'tab',
                aliases: ['labels', 'subtags', 'organize'],
            },
            {
                key: 'account-preferences-watchlists',
                label: 'Watchlists',
                route: '/settings/account/preferences/watchlists',
                type: 'tab',
                aliases: ['companies', 'tracking', 'crosshair'],
            },
        ],
    },
    {
        key: 'account-notifications',
        label: 'Notifications',
        route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.root,
        sectionKey: 'account',
        type: 'page',
        icon: <LucideBell size='1rem' style={iconStyle} />,
        aliases: ['alerts', 'digest', 'email notifications'],
        children: [
            {
                key: 'account-notifications-general-tab',
                label: 'General',
                route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                type: 'tab',
                aliases: ['notification preferences', 'delivery settings'],
            },
            {
                key: 'account-notifications-alerts-tab',
                label: 'Alerts',
                route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.alerts,
                type: 'tab',
                aliases: ['custom alerts', 'event-driven alerts', 'monitors'],
            },
            {
                key: 'account-notifications-methods',
                label: 'Methods',
                route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                type: 'section',
                aliases: ['email', 'push', 'delivery methods'],
                children: [
                    {
                        key: 'account-notifications-method-email',
                        label: 'Email',
                        route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                        type: 'field',
                    },
                    {
                        key: 'account-notifications-method-inApp',
                        label: 'In-App',
                        route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                        type: 'field',
                    },
                    {
                        key: 'account-notifications-method-push',
                        label: 'Push',
                        route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                        type: 'field',
                    },
                    {
                        key: 'account-notifications-method-slack',
                        label: 'Slack',
                        route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                        type: 'field',
                        aliases: ['Slack channels', 'Slack notifications'],
                    },
                ],
            },
            {
                key: 'account-notifications-digests',
                label: 'Digests',
                route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                type: 'section',
                aliases: ['workspace digest', 'daily digest', 'weekly digest'],
                children: [
                    {
                        key: 'account-notifications-digest-workspace',
                        label: 'Workspace Digest',
                        route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                        type: 'field',
                    },
                ],
            },
            {
                key: 'account-notifications-general',
                label: 'General',
                route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                type: 'section',
                aliases: ['notify me about', 'notification workflows'],
                children: notificationSettingsGroups.map((group) => ({
                    key: `account-notifications-${group.key}`,
                    label: group.title,
                    route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                    type: 'section' as const,
                    children: group.options.map((option) => ({
                        key: `account-notifications-${option.key}`,
                        label: option.title,
                        description: option.description,
                        route: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                        type: 'field' as const,
                    })),
                })),
            },
        ],
    },
    {
        key: 'account-sessions',
        label: 'Sessions',
        route: '/settings/account/sessions',
        sectionKey: 'account',
        type: 'page',
        icon: <TbDeviceDesktop size='1rem' style={iconStyle} />,
        aliases: ['devices', 'login sessions', 'active sessions'],
        children: [
            {
                key: 'account-sessions-current',
                label: 'Current Sessions',
                route: '/settings/account/sessions',
                type: 'section',
                aliases: ['revoke session', 'device session', 'browser session'],
            },
        ],
    },
    {
        key: 'account-security',
        label: 'Security',
        route: '/settings/account/security',
        sectionKey: 'account',
        type: 'page',
        icon: <TbShieldLock size='1rem' style={iconStyle} />,
        aliases: ['authentication', 'two-factor authentication', 'passkeys'],
        children: [
            {
                key: 'account-security-authentication',
                label: 'Preferred Authentication Method',
                route: '/settings/account/security',
                type: 'section',
                aliases: ['password', 'passkeys', 'login methods'],
                children: [
                    {
                        key: 'account-security-passkeys',
                        label: 'Passkeys',
                        route: '/settings/account/security',
                        type: 'field',
                    },
                ],
            },
            {
                key: 'account-security-two-factor',
                label: 'Two-Factor Authentication',
                route: '/settings/account/security',
                type: 'section',
                aliases: ['2fa', 'tfa', 'mfa'],
            },
            {
                key: 'account-security-download',
                label: 'Account & Workspace Data Export',
                route: '/settings/account/security',
                type: 'section',
                aliases: ['export account data', 'download data'],
            },
        ],
    },
    {
        key: 'account-plans',
        label: 'Plans & Billing',
        route: '/settings/account/plans',
        sectionKey: 'account',
        type: 'page',
        icon: <TbChecklist size='1rem' style={iconStyle} />,
        aliases: ['billing', 'plans', 'subscription', 'invoices'],
        children: [
            {
                key: 'account-plans-billing',
                label: 'Billing',
                route: '/settings/account/plans/billing',
                type: 'tab',
                icon: <TbCreditCard size='1rem' style={iconStyle} />,
                aliases: ['subscription', 'payment method', 'stripe'],
                children: [
                    {
                        key: 'account-plans-billing-section',
                        label: 'Billing',
                        route: '/settings/account/plans/billing',
                        type: 'section',
                        children: [
                            {
                                key: 'account-plans-summary',
                                label: 'Summary',
                                route: '/settings/account/plans/billing',
                                type: 'field',
                            },
                            {
                                key: 'account-plans-invoice-breakdown',
                                label: 'Invoice Breakdown',
                                route: '/settings/account/plans/billing',
                                type: 'field',
                            },
                            {
                                key: 'account-plans-manage',
                                label: 'Manage your Plan',
                                route: '/settings/account/plans/billing',
                                type: 'field',
                            },
                            {
                                key: 'account-plans-billing-address',
                                label: 'Billing Address',
                                route: '/settings/account/plans/billing',
                                type: 'field',
                            },
                        ],
                    },
                ],
            },
            {
                key: 'account-plans-seats',
                label: 'Seats',
                route: '/settings/account/plans/seats',
                type: 'tab',
                aliases: ['team seats', 'purchase seats', 'users'],
                children: [
                    {
                        key: 'account-plans-seats-section',
                        label: 'Seats',
                        route: '/settings/account/plans/seats',
                        type: 'section',
                        children: [
                            {
                                key: 'account-plans-add-seats',
                                label: 'Add Seats to Plan',
                                route: '/settings/account/plans/seats',
                                type: 'field',
                            },
                        ],
                    },
                ],
            },
            {
                key: 'account-plans-addons',
                label: 'Add-Ons',
                route: '/settings/account/plans/addons',
                type: 'tab',
                icon: <TbPuzzle size='1rem' style={iconStyle} />,
                aliases: ['feature add-ons', 'relations', 'vantage'],
                children: [
                    {
                        key: 'account-plans-addons-section',
                        label: 'Add-Ons',
                        route: '/settings/account/plans/addons',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'account-plans-invoices',
                label: 'Invoices',
                route: '/settings/account/plans/invoices',
                type: 'tab',
                aliases: ['invoice history', 'receipts'],
                children: [
                    {
                        key: 'account-plans-invoices-section',
                        label: 'Invoices',
                        route: '/settings/account/plans/invoices',
                        type: 'section',
                    },
                ],
            },
        ],
    },
    {
        key: 'account-referrals',
        label: 'Refer & Earn',
        route: '/settings/account/referrals',
        sectionKey: 'account',
        type: 'page',
        icon: <TbGift size='1rem' style={iconStyle} />,
        aliases: ['referrals', 'referral program', 'invite and earn', 'partner program'],
        children: [
            {
                key: 'account-referrals-link',
                label: 'Referral Link',
                route: '/settings/account/referrals',
                type: 'section',
                aliases: ['referral code', 'copy referral link', 'dub'],
            },
            {
                key: 'account-referrals-terms',
                label: 'Referral Program Terms & Conditions',
                route: '/settings/account/referrals',
                type: 'section',
                aliases: ['affiliate commission', 'commission', 'policy', 'terms'],
            },
        ],
    },
    {
        key: 'workspace-general',
        label: 'General',
        route: '/settings/workspace/general',
        sectionKey: 'workspace',
        type: 'page',
        icon: <TbSettingsSearch size='1rem' style={iconStyle} />,
        aliases: ['workspace information', 'workspace settings'],
        children: [
            {
                key: 'workspace-general-overview',
                label: 'Overview',
                route: '/settings/workspace/general/overview',
                type: 'tab',
                icon: <TbFocusCentered size='1rem' style={iconStyle} />,
                aliases: ['workspace profile', 'workspace avatar'],
                children: [
                    {
                        key: 'workspace-general-workspace-information',
                        label: 'Workspace Information',
                        route: '/settings/workspace/general/overview',
                        type: 'section',
                        aliases: ['workspace profile', 'workspace identity'],
                        children: [
                            {
                                key: 'workspace-general-avatar',
                                label: 'Avatar',
                                route: '/settings/workspace/general/overview',
                                type: 'field',
                            },
                            {
                                key: 'workspace-general-name',
                                label: 'Workspace Name',
                                route: '/settings/workspace/general/overview',
                                type: 'field',
                            },
                        ],
                    },
                    {
                        key: 'workspace-general-support',
                        label: 'Support',
                        route: '/settings/workspace/general/overview',
                        type: 'section',
                        children: [
                            {
                                key: 'workspace-general-support-slack',
                                label: 'Shared Slack Channel',
                                route: '/settings/workspace/general/overview',
                                type: 'field',
                            },
                            {
                                key: 'workspace-general-support-contact',
                                label: 'Contact Support',
                                route: '/settings/workspace/general/overview',
                                type: 'field',
                                aliases: ['email support', 'help'],
                            },
                        ],
                    },
                    {
                        key: 'workspace-general-danger-zone',
                        label: 'Danger Zone',
                        route: '/settings/workspace/general/overview',
                        type: 'section',
                        aliases: ['delete workspace', 'leave workspace'],
                    },
                ],
            },
            {
                key: 'workspace-general-preferences',
                label: 'Preferences',
                route: '/settings/workspace/general/preferences',
                type: 'tab',
                aliases: ['remove branding', 'public resources'],
                children: [
                    {
                        key: 'workspace-general-preferences-branding',
                        label: 'Remove Branding',
                        route: '/settings/workspace/general/preferences',
                        type: 'section',
                    },
                ],
            },
        ],
    },
    {
        key: 'workspace-advanced',
        label: 'Advanced',
        route: '/settings/workspace/advanced',
        sectionKey: 'workspace',
        type: 'page',
        icon: <TbAdjustments size='1rem' style={iconStyle} />,
        aliases: ['workspace advanced settings', 'migration assistance'],
        children: [
            {
                key: 'workspace-advanced-appearance',
                label: 'Appearance',
                route: '/settings/workspace/advanced/appearance',
                type: 'tab',
                icon: <TbBrush size='1rem' style={iconStyle} />,
                aliases: ['whitelabeling', 'theme'],
                children: [
                    {
                        key: 'workspace-advanced-appearance-section',
                        label: 'Appearance',
                        route: '/settings/workspace/advanced/appearance',
                        type: 'section',
                        children: [
                            {
                                key: 'workspace-advanced-whitelabeling',
                                label: 'Whitelabeling',
                                route: '/settings/workspace/advanced/appearance',
                                type: 'field',
                                aliases: ['brand color', 'company logo', 'company website'],
                            },
                        ],
                    },
                ],
            },
            {
                key: 'workspace-advanced-usage',
                label: 'Usage',
                route: '/settings/workspace/advanced/usage',
                type: 'tab',
                aliases: ['workspace usage', 'events'],
                children: [
                    {
                        key: 'workspace-advanced-usage-overview',
                        label: 'Overview',
                        route: '/settings/workspace/advanced/usage',
                        type: 'section',
                        children: [
                            {
                                key: 'workspace-advanced-usage-active-users',
                                label: 'Active Users',
                                route: '/settings/workspace/advanced/usage',
                                type: 'field',
                            },
                            {
                                key: 'workspace-advanced-usage-active-guests',
                                label: 'Active Guests',
                                route: '/settings/workspace/advanced/usage',
                                type: 'field',
                            },
                            {
                                key: 'workspace-advanced-usage-locations',
                                label: 'Event Locations',
                                route: '/settings/workspace/advanced/usage',
                                type: 'field',
                            },
                            {
                                key: 'workspace-advanced-usage-timeline',
                                label: 'Event Timeline',
                                route: '/settings/workspace/advanced/usage',
                                type: 'field',
                            },
                        ],
                    },
                ],
            },
            {
                key: 'workspace-advanced-access',
                label: 'Access',
                route: '/settings/workspace/advanced/access',
                type: 'tab',
                icon: <TbLockAccess size='1rem' style={iconStyle} />,
                aliases: ['invites', 'domains', 'workspace access'],
                children: [
                    {
                        key: 'workspace-advanced-access-section',
                        label: 'Access',
                        route: '/settings/workspace/advanced/access',
                        type: 'section',
                        children: [
                            {
                                key: 'workspace-advanced-custom-subdomains',
                                label: 'Custom Subdomains',
                                route: '/settings/workspace/advanced/access',
                                type: 'field',
                            },
                            {
                                key: 'workspace-advanced-network-access',
                                label: 'Network Access',
                                route: '/settings/workspace/advanced/access',
                                type: 'field',
                            },
                            {
                                key: 'workspace-advanced-authorized-methods',
                                label: 'Authorized Methods',
                                route: '/settings/workspace/advanced/access',
                                type: 'field',
                            },
                        ],
                    },
                ],
            },
            {
                key: 'workspace-advanced-identity',
                label: 'Identity',
                route: '/settings/workspace/advanced/identity',
                type: 'tab',
                icon: <LucideShieldCheck size='1rem' style={iconStyle} />,
                aliases: ['workos', 'directory sync', 'sso'],
                children: [
                    {
                        key: 'workspace-advanced-identity-section',
                        label: 'Identity',
                        route: '/settings/workspace/advanced/identity',
                        type: 'section',
                        children: [
                            {
                                key: 'workspace-advanced-directory-sync',
                                label: 'SCIM & Directory Sync',
                                route: '/settings/workspace/advanced/identity',
                                type: 'field',
                            },
                            {
                                key: 'workspace-advanced-session-duration',
                                label: 'User Session Duration',
                                route: '/settings/workspace/advanced/identity',
                                type: 'field',
                            },
                            {
                                key: 'workspace-advanced-force-logout',
                                label: 'Force All Users to Logout',
                                route: '/settings/workspace/advanced/identity',
                                type: 'field',
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        key: 'workspace-permissions',
        label: 'Permissions',
        route: '/settings/workspace/permissions',
        sectionKey: 'workspace',
        type: 'page',
        icon: <TbPyramid size='1rem' style={iconStyle} />,
        aliases: ['rbac', 'roles', 'members', 'teams'],
        children: [
            {
                key: 'workspace-permissions-members',
                label: 'Members',
                route: '/settings/workspace/permissions/members',
                type: 'tab',
                icon: <TbUsersGroup size='1rem' style={iconStyle} />,
                children: [
                    {
                        key: 'workspace-permissions-members-section',
                        label: 'Members',
                        route: '/settings/workspace/permissions/members',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'workspace-permissions-roles',
                label: 'Roles',
                route: '/settings/workspace/permissions/roles',
                type: 'tab',
                icon: <TbUserShield size='1rem' style={iconStyle} />,
                aliases: ['custom roles', 'permissions'],
                children: [
                    {
                        key: 'workspace-permissions-roles-section',
                        label: 'Roles',
                        route: '/settings/workspace/permissions/roles',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'workspace-permissions-teams',
                label: 'Teams',
                route: '/settings/workspace/permissions/teams',
                type: 'tab',
                icon: <TbUsers size='1rem' style={iconStyle} />,
                children: [
                    {
                        key: 'workspace-permissions-teams-section',
                        label: 'Teams',
                        route: '/settings/workspace/permissions/teams',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'workspace-permissions-domains',
                label: 'Verified Domains',
                route: '/settings/workspace/permissions/domains',
                type: 'tab',
                icon: <TbWorldCheck size='1rem' style={iconStyle} />,
                aliases: ['domains', 'email domains'],
                children: [
                    {
                        key: 'workspace-permissions-domains-section',
                        label: 'Verified Domains',
                        route: '/settings/workspace/permissions/domains',
                        type: 'section',
                    },
                ],
            },
        ],
    },
    {
        key: 'workspace-workflows',
        label: 'Workflows',
        route: '/settings/workspace/workflows',
        sectionKey: 'workspace',
        type: 'page',
        icon: <TbPlugConnected size='1rem' style={iconStyle} />,
        aliases: ['integrations', 'zapier', 'automation'],
        children: [
            {
                key: 'workspace-workflows-integrations',
                label: 'Workspace Integrations',
                route: '/settings/workspace/workflows',
                type: 'section',
                aliases: ['countercyclical integrations', 'zapier integration'],
            },
        ],
    },
    {
        key: 'workspace-developers',
        label: 'Developers',
        route: '/settings/workspace/developers',
        sectionKey: 'workspace',
        type: 'page',
        icon: <TbCode size='1rem' style={iconStyle} />,
        aliases: ['api', 'webhooks', 'oauth'],
        children: [
            {
                key: 'workspace-developers-api',
                label: 'API Keys',
                route: '/settings/workspace/developers/api',
                type: 'tab',
                icon: <LucideKeyRound size='0.875rem' style={iconStyle} />,
                aliases: ['developer keys', 'personal access tokens'],
                children: [
                    {
                        key: 'workspace-developers-api-section',
                        label: 'API Keys',
                        route: '/settings/workspace/developers/api',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'workspace-developers-webhooks',
                label: 'Webhooks',
                route: '/settings/workspace/developers/webhooks',
                type: 'tab',
                icon: <TbWebhook size='1rem' style={iconStyle} />,
                children: [
                    {
                        key: 'workspace-developers-webhooks-section',
                        label: 'Webhooks',
                        route: '/settings/workspace/developers/webhooks',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'workspace-developers-applications',
                label: 'OAuth Applications',
                route: '/settings/workspace/developers/applications',
                type: 'tab',
                icon: <TbBrowserCheck size='1rem' style={iconStyle} />,
                aliases: ['oauth apps', 'applications'],
                children: [
                    {
                        key: 'workspace-developers-applications-section',
                        label: 'OAuth Applications',
                        route: '/settings/workspace/developers/applications',
                        type: 'section',
                    },
                ],
            },
        ],
    },
    {
        key: 'workspace-security',
        label: 'Security',
        route: '/settings/workspace/security',
        sectionKey: 'workspace',
        type: 'page',
        icon: <TbShieldLock size='1rem' style={iconStyle} />,
        aliases: ['saml', 'audit logs', 'event streaming', 'data retention', 'legal hold'],
        children: [
            {
                key: 'workspace-security-overview',
                label: 'Overview',
                route: '/settings/workspace/security/overview',
                type: 'tab',
                icon: <TbCloudLock size='1rem' style={iconStyle} />,
                aliases: ['saml sso', 'single sign-on'],
                children: [
                    {
                        key: 'workspace-security-saml',
                        label: 'SAML SSO',
                        route: '/settings/workspace/security/overview',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'workspace-security-data-retention',
                label: 'Data Retention',
                route: '/settings/workspace/security/retention',
                type: 'tab',
                icon: <FileLock size='1rem' style={iconStyle} />,
                aliases: ['retention policy', 'records retention', 'legal hold', 'data disposal'],
                children: [
                    {
                        key: 'workspace-security-data-retention-policy',
                        label: 'Retention Schedule',
                        route: '/settings/workspace/security/retention',
                        type: 'section',
                        aliases: ['trash retention', 'audit log retention', 'activity history', 'version history'],
                    },
                    {
                        key: 'workspace-security-data-retention-legal-hold',
                        label: 'Legal Hold',
                        route: '/settings/workspace/security/retention',
                        type: 'field',
                        aliases: ['preservation hold', 'suspend deletion'],
                    },
                ],
            },
            {
                key: 'workspace-security-audit-logs',
                label: 'Audit Logs',
                route: '/settings/workspace/security/logs',
                type: 'tab',
                icon: <TbArticle size='1rem' style={iconStyle} />,
                children: [
                    {
                        key: 'workspace-security-audit-logs-section',
                        label: 'Audit Logs',
                        route: '/settings/workspace/security/logs',
                        type: 'section',
                    },
                ],
            },
            {
                key: 'workspace-security-event-streaming',
                label: 'Event Streaming',
                route: '/settings/workspace/security/events',
                type: 'tab',
                icon: <TbCloudUpload size='1rem' style={iconStyle} />,
                aliases: ['audit log streaming', 'generic https', 'https'],
                children: [
                    {
                        key: 'workspace-security-event-streaming-section',
                        label: 'Audit Log Streaming',
                        route: '/settings/workspace/security/events',
                        type: 'section',
                    },
                ],
            },
        ],
    },
    {
        key: 'workspace-about',
        label: 'About',
        route: '/settings/workspace/about',
        sectionKey: 'workspace',
        type: 'page',
        icon: <TbInfoCircle size='1rem' style={iconStyle} />,
        aliases: ['workspace about', 'workspace metadata'],
    },
];
